import { useDispatch } from 'react-redux';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { SimulateCustomPreset } from 'dashboard/models/simulate/preset';
import { showMessage } from 'dashboard/redux/actions/messages';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import { ApiEndpoint, PresetType } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type LoadSimulatePresetsFilter = {
  type: PresetType[];
};

interface ListSimulatePresetsOptions {
  filter: LoadSimulatePresetsFilter;
  sortParams: SortParams;
}

export default (): {
  handleLoadSimulatePresets: ({
    filter,
    sortParams,
  }: {
    filter: LoadSimulatePresetsFilter;
    sortParams: SortParams;
  }) => Promise<SimulateCustomPreset[]>;
  loadingSimulatePresets: boolean;
} => {
  const dispatch = useDispatch();

  const {
    loading: loadingSimulatePresets,
    error: handleLoadSimulatePresetsError,
    callback: handleLoadSimulatePresets,
  } = useApiCallback(async ({ filter, sortParams }: ListSimulatePresetsOptions) => {
    const { data: simulatePresets } = await SessionClientDashboard.listResource<
      SimulateCustomPreset,
      ListSimulatePresetsOptions
    >(ApiEndpoint.SimulatePreset, {
      filter,
      sort: `${sortParams.shouldSortDescending ? '-' : ''}${sortParams.sort}`,
    });
    return simulatePresets;
  }, []);

  useApiErrorHandler({
    error: handleLoadSimulatePresetsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        dispatch(showMessage(permissionsErrorMessage(error), MessageType.Failure));
      }

      dispatch(showMessage('Failed to load simulate presets information.', MessageType.Failure));
    },
    onNetworkError: () =>
      dispatch(
        showMessage('We are experiencing network issues. Please try again.', MessageType.Failure),
      ),
  });

  return {
    loadingSimulatePresets,
    handleLoadSimulatePresets,
  };
};
